/* jshint esversion: 6 */
import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { initiateSocket, getSocket } from "../socket";
import { MyContext } from "../Context/MyProvider";
import { IoChatbubblesSharp, IoClose, IoPaperPlane } from "react-icons/io5";
import { FaCommentAlt, FaUser } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useDraggableWidget } from "../hooks/useDraggableWidget";

const GlobalChatWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { basUrl, imageBaseURL, setChatUserName, setChatId, chatId } = useContext(MyContext);

  const { position, bindPointer, wasDragged } = useDraggableWidget("floating_chat_pos", { bottom: 24, right: 24 }, 56, 56);

  const [isWidgetDismissed, setIsWidgetDismissed] = useState(() => {
    return sessionStorage.getItem("hide_floating_chat") === "true";
  });
  const [toasts, setToasts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [recentChats, setRecentChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);

  const drawerRef = useRef(null);

  const getRegisterUser = () => {
    try {
      const localData = localStorage.getItem("Register_User");
      if (localData) return JSON.parse(localData);
      const userLogin = localStorage.getItem("UserLogin");
      if (userLogin) return JSON.parse(userLogin);
      return null;
    } catch {
      return null;
    }
  };

  const currentUser = getRegisterUser();
  const token = localStorage.getItem("token");

  // Fetch recent chat list for drawer
  const fetchRecentChats = async () => {
    const userData = getRegisterUser();
    if (!userData?.id) return;
    setLoadingChats(true);
    try {
      const res = await axios.post(`${basUrl}my_chats`, { uid: userData.id });
      if (res.data?.Result === "true" || res.data?.ResponseCode === "200") {
        const rawList = res.data.chatlist || res.data.chat_list || res.data.data || [];
        const parsedChats = rawList.map((c) => ({
          otherId: c.uid || c.profile_id || c.otherId || c.id,
          otherName: c.name || c.profile_name || c.otherName || t("User"),
          otherPic: c.pro_pic || c.profile_pic || c.otherPic || "",
          lastMessage: c.lastMessage || c.message || c.last_msg || t("Tap to start chatting"),
          timestamp: c.timestamp || "",
        }));
        setRecentChats(parsedChats);
      }
    } catch (err) {
      console.error("Error fetching recent chats:", err);
    } finally {
      setLoadingChats(false);
    }
  };

  // Socket connection & real-time message listener
  useEffect(() => {
    if (!currentUser?.id) return;

    const socket = initiateSocket(currentUser.id);

    if (socket) {
      const handleNewMessage = (data) => {
        // If user is not currently viewing the sender's chat
        if (location.pathname !== "/chat" || String(chatId) !== String(data.sender_id)) {
          // Increment unread counter
          setUnreadCount((prev) => prev + 1);

          // Add popover toast
          const newToast = {
            id: Date.now() + Math.random(),
            senderId: data.sender_id,
            senderName: data.sender_name || t("Someone"),
            senderPic: data.sender_pic || "",
            message: data.message || t("Sent a message"),
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          };

          setToasts((prev) => [newToast, ...prev.slice(0, 4)]); // Keep max 5 toasts
        }

        // Refresh recent chat list if drawer is open
        if (isDrawerOpen) {
          fetchRecentChats();
        }
      };

      socket.on("messageRecieve", handleNewMessage);
      socket.on("chat_notification", handleNewMessage);

      return () => {
        socket.off("messageRecieve", handleNewMessage);
        socket.off("chat_notification", handleNewMessage);
      };
    }
  }, [currentUser?.id, location.pathname, chatId, isDrawerOpen]);

  // Click outside to close drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };
    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDrawerOpen]);

  const toggleDrawer = () => {
    if (!isDrawerOpen) {
      fetchRecentChats();
      setUnreadCount(0); // Reset unread counter when drawer opened
    }
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleOpenChat = (otherId, otherName) => {
    setChatId(otherId);
    if (setChatUserName) setChatUserName(otherName);
    setIsDrawerOpen(false);
    setToasts([]);
    navigate("/chat");
  };

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Hide floating widget if user is logged out or dismissed widget for this session
  if (!token || !currentUser?.id || isWidgetDismissed) return null;

  const isChatPage = location.pathname === "/chat";

  return (
    <>
      {/* FLOATING TOAST NOTIFICATIONS */}
      <div className="fixed top-20 right-5 z-[99999] flex flex-col gap-3 max-w-[360px] w-full pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto bg-white/95 backdrop-blur-md border border-purple-200/80 shadow-2xl rounded-2xl p-3.5 flex flex-col gap-2 transition-all duration-300 animate-in slide-in-from-top-5 relative overflow-hidden"
          >
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                {toast.senderPic ? (
                  <img
                    src={`${imageBaseURL}${toast.senderPic}`}
                    alt={toast.senderName}
                    className="w-10 h-10 rounded-full object-cover border-2 border-purple-500 shadow-sm"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/150";
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                    <FaUser size={16} />
                  </div>
                )}
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <h6 className="text-xs font-bold text-gray-900 truncate m-0">
                    {toast.senderName}
                  </h6>
                  <span className="text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full shrink-0">
                    {toast.time}
                  </span>
                </div>
                <p className="text-xs text-gray-600 truncate m-0 mt-0.5 font-medium">
                  {toast.message}
                </p>
              </div>

              <button
                onClick={() => dismissToast(toast.id)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors shrink-0"
              >
                <IoClose size={16} />
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-gray-100">
              <button
                onClick={() => dismissToast(toast.id)}
                className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t("Dismiss")}
              </button>
              <button
                onClick={() => handleOpenChat(toast.senderId, toast.senderName)}
                className="px-4 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 active:scale-95"
              >
                <IoPaperPlane size={13} />
                <span>{t("Reply")}</span>
              </button>
            </div>

            {/* Auto dismiss progress bar */}
            <div className="absolute bottom-0 left-0 h-0.5 bg-purple-500/40 w-full animate-toast-progress" />
          </div>
        ))}
      </div>

      {/* STICKY DRAGGABLE FLOATING CHAT ICON & QUICK DRAWER */}
      {!isChatPage && (
        <div
          ref={drawerRef}
          style={{
            position: "fixed",
            left: `${position.x}px`,
            top: `${position.y}px`,
            touchAction: "none",
            userSelect: "none",
            zIndex: 9999,
          }}
        >
          {/* QUICK CHAT POPOVER DRAWER */}
          {isDrawerOpen && (
            <div
              className={`absolute w-[320px] bg-white rounded-2xl shadow-2xl border border-purple-100 overflow-hidden flex flex-col transition-all duration-300 animate-in slide-in-from-bottom-4 ${
                position.y < 350 ? "top-16" : "bottom-16"
              } ${position.x < 350 ? "left-0" : "right-0"}`}
              style={{
                boxShadow: "0 20px 40px -10px rgba(152, 14, 255, 0.3), 0 0 20px rgba(0,0,0,0.06)"
              }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 p-4 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <IoChatbubblesSharp className="text-xl" />
                  <span className="font-bold text-sm">{t("Recent Messages")}</span>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                >
                  <IoClose size={18} />
                </button>
              </div>

              {/* Body Chat List */}
              <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-100 p-2">
                {loadingChats ? (
                  <div className="py-8 text-center text-xs text-gray-400">
                    {t("Loading messages...")}
                  </div>
                ) : recentChats.length === 0 ? (
                  <div className="py-8 text-center text-xs text-gray-400 flex flex-col items-center gap-2">
                    <FaCommentAlt className="text-2xl text-purple-200" />
                    <span>{t("No recent conversations")}</span>
                  </div>
                ) : (
                  recentChats.map((chat) => {
                    const otherId = chat.otherId || chat.uid || chat.id;
                    const otherName = chat.otherName || chat.name || t("User");
                    const otherPic = chat.otherPic || chat.pro_pic || "";
                    const lastMsgText = chat.lastMessage || chat.message || t("Tap to start chatting");

                    return (
                      <div
                        key={otherId}
                        onClick={() => handleOpenChat(otherId, otherName)}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-purple-50/70 transition-all cursor-pointer group"
                      >
                        <div className="relative shrink-0">
                          {otherPic ? (
                            <img
                              src={`${imageBaseURL}${otherPic}`}
                              alt={otherName}
                              className="w-10 h-10 rounded-full object-cover border border-purple-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/150";
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-sm">
                              {otherName.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <h6 className="text-xs font-bold text-gray-800 truncate m-0 group-hover:text-purple-600 transition-colors">
                              {otherName}
                            </h6>
                          </div>
                          <p className="text-[11px] text-gray-500 truncate m-0 mt-0.5">
                            {lastMsgText}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* FLOATING ACTION BUTTON */}
          <div className="relative group">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                sessionStorage.setItem("hide_floating_chat", "true");
                setIsWidgetDismissed(true);
              }}
              className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-gray-900/90 hover:bg-red-500 text-white rounded-full flex items-center justify-center shadow-md border border-white z-20 cursor-pointer transition-colors"
              title={t("Hide widget until next login")}
            >
              <IoClose size={12} />
            </button>

            <button
              {...bindPointer}
              onClick={(e) => {
                if (wasDragged()) return;
                toggleDrawer();
              }}
              className="w-14 h-14 bg-gradient-to-tr from-purple-600 via-purple-700 to-indigo-600 text-white rounded-full shadow-2xl flex items-center justify-center transition-transform duration-200 hover:scale-110 active:scale-95 relative border-2 border-white/40 cursor-grab active:cursor-grabbing"
              style={{
                boxShadow: "0 10px 25px -3px rgba(152, 14, 255, 0.5), 0 4px 10px rgba(0,0,0,0.2)"
              }}
              title={t("Quick Chat (Drag to move)")}
            >
              <IoChatbubblesSharp className="text-2xl transition-transform duration-300" />

              {/* UNREAD BADGE */}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white font-extrabold text-[11px] min-w-[22px] h-[22px] px-1 rounded-full flex items-center justify-center border-2 border-white shadow-md animate-pulse">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}

              {/* Subtle pulse ring */}
              <span className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping -z-1" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalChatWidget;
